import './SupportRequest.scss'

import { useEffect, useState } from 'react'
import {
  Button,
  Container,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner
} from 'react-bootstrap'

import parse from 'html-react-parser'

import { getAuthenticatedUser } from '../../auth/'

import AccountMenu from '../../common/navigation/AccountMenu'
import Error from '../../common/error/Error'
import { onSupportRequest } from 'components/common/api/Support'
import { isInApp, openUrlInSafari } from 'components/common/helpers/Mobile'
import { useLocation } from 'react-router-dom'
import AccountDeleteNotice from 'components/account/AccountDeleteNotice/AccountDeleteNotice'

/**
 * Simple Support Request component for collecting user support requests
 */
export default function SupportRequest({ setHeaderTitle, setReturnPath }) {
  // Did we redirect with a request type?
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const requestType = searchParams.get('requestType') || 'general'
  const isDeleteRequest = requestType === 'delete'

  const { display_name, email } = getAuthenticatedUser()
  const [error, setError] = useState()
  const [success, setSuccess] = useState()
  const [loading, setLoading] = useState()
  const [requestMessage, setRequestMessage] = useState(
    isDeleteRequest ? 'I would like to delete my account.' : ''
  )

  /**
   * Helper function to handle clicks on email links
   * @param {Event} event The click event
   * @param {String} url The URL to open
   */
  const handleEmailClick = (event, url) => {
    // If we are in the app and the user clicks on the privacy policy link, let's open the link in a native window
    if (isInApp()) {
      event.preventDefault()
      event.stopPropagation()
      openUrlInSafari(url)
    }
  }

  /**
   * Handle the form submit
   * @returns
   */
  const handleClick = async () => {
    // Return early if we don't have a message
    if (!requestMessage) return
    setLoading(true)

    const message = isDeleteRequest
      ? `Deletion Request: ${requestMessage}`
      : requestMessage

    const body = {
      name: display_name,
      email,
      message
    }

    const response = await onSupportRequest(body)
    const success = response.status === 'success'

    // Update the state
    setSuccess(success)
    setError(!success)
    setLoading(false)
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Update header and return path
    setHeaderTitle('Support')
    setReturnPath(null)
  }, [setHeaderTitle, setReturnPath])

  return (
    <Container className='support-request-container'>
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8}>
          <Row>
            <Col>
              <h1 className='title'>
                {isDeleteRequest ? 'Delete Account' : 'How can we help?'}
              </h1>
              <p>
                Please enter the details of your{' '}
                {isDeleteRequest ? 'delete' : 'support'} request below or simply
                email{' '}
                <a
                  href='mailto:support@remble.com'
                  target='_blank'
                  rel='noreferrer'
                  onClick={e =>
                    handleEmailClick(e, 'mailto:support@remble.com')
                  }
                >
                  support@remble.com
                </a>{' '}
                and a member of our team will respond as soon as possible.
              </p>
            </Col>
          </Row>
          <Row>
            <hr className='my-4' />
            <Col>
              <h5>Your Name</h5>
              <p className='muted'>{display_name}</p>
            </Col>
            <Col>
              <h5>Your Email</h5>
              <p className='muted'>{email}</p>
            </Col>
          </Row>
          <Row>
            <Col>
              {!success ? (
                <>
                  <h5>Your Message</h5>
                  {/* Show errors if we have any */}
                  <Error error={error} />
                  <Form.Group
                    controlId='formMessage'
                    className='form-group visible'
                  >
                    <FloatingLabel
                      controlId='floatingMessage'
                      label='How can we help?'
                    >
                      <Form.Control
                        disabled={loading}
                        size='lg'
                        placeholder={
                          isDeleteRequest
                            ? 'Delete request'
                            : 'How can we help?'
                        }
                        required
                        as='textarea'
                        className='support-request-textarea'
                        onChange={e => setRequestMessage(e.currentTarget.value)}
                        defaultValue={requestMessage}
                      />
                    </FloatingLabel>
                  </Form.Group>
                  {isDeleteRequest && (
                    <>
                      <p>Please allow up to 7 days for account deletion.</p>
                      <p>
                        <strong>
                          Note that deleting your account is permanent and
                          cannot be undone.
                        </strong>
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className='success-message'>
                    Thank you {display_name}! Your message was sent to our
                    Support team.
                  </p>
                  <h5>Your Message</h5>
                  <div className='request-message'>
                    {/* Convert line breaks */}
                    {parse(requestMessage.replace('\n', '<br />'))}
                  </div>
                </>
              )}

              <Button
                size='lg'
                variant={isDeleteRequest ? 'danger' : 'primary'}
                className='w-100 mt-3'
                onClick={handleClick}
                disabled={loading || !requestMessage}
                hidden={success}
              >
                <Spinner
                  animation='border'
                  role='status'
                  size='sm'
                  style={{ margin: '0 10px 2px 0' }}
                  hidden={!loading}
                >
                  <span className='visually-hidden'>Loading...</span>
                </Spinner>
                <span>
                  {isDeleteRequest
                    ? 'Delete My Account'
                    : 'Send Support Request'}
                </span>
              </Button>
            </Col>
          </Row>

          {/* Show account delete notice if user is making a normal request */}
          {!isDeleteRequest && <AccountDeleteNotice />}
        </Col>
      </Row>
    </Container>
  )
}
